/**
 * Inline debug panel that surfaces the intent router + advisor per-turn trace
 * below each coach message. Rendered on every coach message; the actual data
 * is only fetched lazily the first time the user expands the panel, so the
 * default-collapsed state has near-zero cost.
 */
import React, { useEffect, useState } from 'react';
import { getIntentRouterTrace, IntentRouterTrace } from '../services/api';

interface Props {
  messageId: number | string;
}

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="mb-2">
    <div className="text-[10px] font-semibold uppercase tracking-wide text-violet-700">{title}</div>
    <div className="text-xs text-slate-700 whitespace-pre-wrap font-mono leading-snug">{children}</div>
  </div>
);

const KV: React.FC<{ k: string; v?: string }> = ({ k, v }) => (
  <div className="grid grid-cols-[140px_1fr] gap-2 py-0.5">
    <div className="text-[11px] text-slate-500">{k}</div>
    <div className="text-[11px] text-slate-900 break-words">{v || <span className="text-slate-400">—</span>}</div>
  </div>
);

// --- Advisor readability helpers (TVG framing: knows / shaky / next / read) ---

// Flatten an advisor field (string | list | object) into one compact line.
// Empty / null / [] collapse to "" so the caller can hide the row entirely.
const fmtNote = (v: any): string => {
  if (v == null) return '';
  if (Array.isArray(v)) {
    return v.length
      ? v.map((x) => (typeof x === 'string' ? x : (x?.topic || x?.name || x?.gap || x?.note || JSON.stringify(x)))).join(' · ')
      : '';
  }
  if (typeof v === 'object') return v.topic || v.name || JSON.stringify(v);
  return String(v);
};

// A labelled row that renders NOTHING when its value is empty — this is what
// keeps the panel from showing "title: null", "gaps: []", etc.
const Row: React.FC<{ k: string; v?: string }> = ({ k, v }) =>
  v && String(v).trim() ? (
    <div className="grid grid-cols-[110px_1fr] gap-2 py-0.5">
      <div className="text-[11px] text-slate-500">{k}</div>
      <div className="text-[11px] text-slate-900 break-words whitespace-pre-wrap">{v}</div>
    </div>
  ) : null;

// A small grouped block with a coloured heading; hides itself if all children
// are empty (React renders nothing for false/null children).
const Group: React.FC<{ title: string; show: boolean; children: React.ReactNode }> = ({ title, show, children }) =>
  show ? (
    <div className="mb-1.5">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-amber-700/80 mb-0.5">{title}</div>
      {children}
    </div>
  ) : null;

// URL-gated: only renders when the chat URL has ?debug=1 or ?debug=true.
// Without the flag the panel disappears entirely — keeps the production UI
// clean while letting devs toggle the trace into view on demand. Re-evaluated
// on every render so navigating to a debug URL flips it on without reload.
const _isDebugEnabled = (): boolean => {
  if (typeof window === "undefined") return false;
  try {
    const v = (new URLSearchParams(window.location.search).get("debug") || "").toLowerCase();
    return v === "1" || v === "true";
  } catch {
    return false;
  }
};

const IntentRouterPanel: React.FC<Props> = ({ messageId }) => {
  // Hooks must run in the same order every render (rules of hooks) — declare
  // them BEFORE the debug-gate early-return. They're cheap: useEffect only
  // does work when `open` is true, and the panel can never open (button is
  // hidden by the gate) so no API call fires when debug is off.
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [trace, setTrace] = useState<IntentRouterTrace | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || trace || loading) return;
    setLoading(true);
    setError(null);
    getIntentRouterTrace(messageId)
      .then((data) => setTrace(data || {}))
      .catch((e) => setError(e?.message || 'failed to load'))
      .finally(() => setLoading(false));
  }, [open, messageId, trace, loading]);

  if (!_isDebugEnabled()) return null;

  const hasRouter = trace && (trace.state || trace.inputs);
  const hasAdvisor = trace?.advisor && (trace.advisor.rendered_block || (trace.advisor.raw_notes && Object.keys(trace.advisor.raw_notes).length > 0));
  const hasProgressReview = trace?.progress_review !== undefined;
  const hasKnowledge = trace?.knowledge !== undefined && (trace.knowledge.state || trace.knowledge.evidence);
  const hasData = hasRouter || hasAdvisor || hasProgressReview || hasKnowledge;

  return (
    <div className="mt-2 ml-10 max-w-3xl">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-[11px] text-slate-500 hover:text-violet-700 underline-offset-2 hover:underline focus:outline-none"
      >
        {open ? '▾' : '▸'} turn debug (intent + advisor)
      </button>

      {open && (
        <div className="mt-1 rounded-md border border-violet-200 bg-violet-50/40 px-3 py-2 text-xs">
          {loading && <div className="text-slate-500">loading trace…</div>}
          {error && <div className="text-red-600">error: {error}</div>}
          {!loading && !error && !hasData && (
            <div className="text-slate-500">No trace persisted for this message (router was disabled or failed).</div>
          )}
          {!loading && !error && hasData && (
            <>
              {trace?.state && (
                <Section title="Router output (state)">
                  <KV k="interest_signal" v={trace.state.interest_signal} />
                  <KV k="student_intent" v={trace.state.student_intent} />
                  <KV k="topic_action" v={trace.state.topic_action} />
                  <KV k="question_policy" v={trace.state.question_policy} />
                  <KV k="response_goal" v={trace.state.response_goal} />
                  <KV k="reason_short" v={trace.state.reason_short} />
                </Section>
              )}

              {trace?.inputs && (
                <Section title="Inputs (variables passed to router this turn)">
                  <KV k="query" v={trace.inputs.query} />
                  <KV k="core_theme" v={trace.inputs.core_theme} />
                  <KV k="student_grade" v={trace.inputs.student_grade_description} />
                  <KV
                    k="history"
                    v={
                      trace.inputs.conversation_history
                        ? `${trace.inputs.conversation_history.slice(0, 400)}${trace.inputs.conversation_history.length > 400 ? '…' : ''}`
                        : ''
                    }
                  />
                </Section>
              )}

              {trace?.guidance_block && (
                <Section title="Guidance block injected into system prompt">
                  {trace.guidance_block.trim()}
                </Section>
              )}

              {(trace?.router_system_prompt || trace?.router_user_prompt) && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-[10px] font-semibold uppercase tracking-wide text-violet-700">
                    Rendered router prompt (system + user)
                  </summary>
                  <div className="mt-1">
                    {trace.router_system_prompt && (
                      <Section title="system">{trace.router_system_prompt}</Section>
                    )}
                    {trace.router_user_prompt && (
                      <Section title="user">{trace.router_user_prompt}</Section>
                    )}
                  </div>
                </details>
              )}

              {hasKnowledge && (
                <div className="mt-3 pt-2 border-t border-violet-200">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-sky-700">
                      Knowledge state (advisor decision)
                    </div>
                    {(() => {
                      const state = (trace?.knowledge?.state || "").toLowerCase();
                      const chip: Record<string, { label: string; cls: string }> = {
                        forgotten:      { label: "FORGOTTEN · was taught before, faded",   cls: "bg-amber-100 text-amber-800 border-amber-300" },
                        never_learned:  { label: "NEVER LEARNED · genuinely new",          cls: "bg-rose-100 text-rose-800 border-rose-300" },
                        partly_solid:   { label: "PARTLY SOLID · grasp with gaps",         cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
                        unknown:        { label: "unknown · not enough evidence yet",      cls: "bg-slate-100 text-slate-600 border-slate-300" },
                      };
                      const c = chip[state] || chip.unknown;
                      return (
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${c.cls}`}>
                          {c.label}
                        </span>
                      );
                    })()}
                  </div>
                  {trace?.knowledge?.evidence && (
                    <div className="text-[11px] text-slate-700 grid grid-cols-[140px_1fr] gap-2">
                      <div className="text-slate-500">evidence</div>
                      <div className="font-mono break-words">{trace.knowledge.evidence}</div>
                    </div>
                  )}
                </div>
              )}

              {hasProgressReview && (
                <div className="mt-3 pt-2 border-t border-violet-200">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                      Progress Review (advisor decision)
                    </div>
                    {(() => {
                      const action = (trace?.progress_review?.next_action || "stay_drill").toLowerCase();
                      const chip: Record<string, { label: string; cls: string }> = {
                        stay_drill:        { label: "stay_drill · keep on current element",         cls: "bg-slate-100 text-slate-600 border-slate-300" },
                        switch_angle:      { label: "SWITCH ANGLE · different tactic, same element", cls: "bg-yellow-100 text-yellow-800 border-yellow-300" },
                        rescue_mode:       { label: "RESCUE · solve it WITH them, step by step",     cls: "bg-orange-100 text-orange-800 border-orange-300" },
                        progress_review:   { label: trace?.progress_review?.forced_by_floor ? "PROGRESS REVIEW · floor-forced (LLM under-fired)" : "PROGRESS REVIEW · recap + 2-3 directions", cls: trace?.progress_review?.forced_by_floor ? "bg-emerald-100 text-emerald-800 border-emerald-300 ring-1 ring-orange-300" : "bg-emerald-100 text-emerald-800 border-emerald-300" },
                        pivot_element:     { label: "PIVOT · move to next core element",             cls: "bg-blue-100 text-blue-800 border-blue-300" },
                        re_anchor:         { label: "RE-ANCHOR · drift detected, return to anchor",  cls: "bg-red-100 text-red-800 border-red-300" },
                        bridge_to_related: { label: "BRIDGE · jump to related topic",                cls: "bg-violet-100 text-violet-800 border-violet-300" },
                        offer_quiz:        { label: "QUIZ · offer quick check",                      cls: "bg-pink-100 text-pink-800 border-pink-300" },
                        ask_student:       { label: "ASK STUDENT · let them choose direction",       cls: "bg-amber-100 text-amber-800 border-amber-300" },
                        wrap_up:           { label: "WRAP UP · student signaled done",               cls: "bg-stone-100 text-stone-700 border-stone-300" },
                      };
                      const c = chip[action] || chip.stay_drill;
                      return (
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${c.cls}`}>
                          {c.label}
                        </span>
                      );
                    })()}
                    {(() => {
                      const dir = trace?.progress_review?.directive_text || "";
                      if (!trace?.progress_review?.due) return null;
                      const isWhyAsk = /intent-capture variant/i.test(dir);
                      return (
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${
                          isWhyAsk
                            ? "bg-amber-100 text-amber-800 border-amber-300"
                            : "bg-slate-100 text-slate-700 border-slate-300"
                        }`}>
                          {isWhyAsk ? "intent-capture variant" : "default variant"}
                        </span>
                      );
                    })()}
                  </div>
                  {/* Active behavior frame steering the coach THIS turn — a 1:1
                      function of the committed student_intent (see
                      _build_intent_mode_block). Shows which "mode" is working. */}
                  {(() => {
                    const mode = (trace?.progress_review?.active_intent_mode
                      || (trace?.advisor?.raw_notes as any)?.student_intent || "").toLowerCase();
                    const chip: Record<string, { label: string; cls: string }> = {
                      exam_revision:      { label: "EXAM-MODE · direct, lock-in over wonder",        cls: "bg-indigo-100 text-indigo-800 border-indigo-300" },
                      pure_curiosity:     { label: "CURIOSITY-MODE · system framing, open-ended",     cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
                      classroom_followup: { label: "CLASSROOM-MODE · anchor in what teacher taught",  cls: "bg-sky-100 text-sky-800 border-sky-300" },
                      casual_question:    { label: "CASUAL-MODE · keep it light",                     cls: "bg-stone-100 text-stone-700 border-stone-300" },
                    };
                    const c = chip[mode];
                    return (
                      <div className="flex items-center gap-2 mb-1">
                        <div className="text-[10px] text-slate-500">active frame</div>
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${c ? c.cls : "bg-slate-100 text-slate-500 border-slate-300"}`}>
                          {c ? c.label : "none · default coach (intent not committed yet)"}
                        </span>
                      </div>
                    );
                  })()}
                  {/* Advisor's student_intent + evidence — surfaced even on
                      non-firing turns since the advisor judges it every turn. */}
                  {(() => {
                    const adv = trace?.advisor?.raw_notes || {};
                    const intent = (adv as any).student_intent;
                    const evidence = (adv as any).intent_evidence;
                    if (!intent && !evidence) return null;
                    return (
                      <div className="mt-1 mb-1 text-[11px] text-slate-700 grid grid-cols-[140px_1fr] gap-2">
                        <div className="text-slate-500">student_intent</div>
                        <div className="font-mono">
                          {String(intent || "—")}
                          {evidence ? <span className="text-slate-400"> · {String(evidence)}</span> : null}
                        </div>
                      </div>
                    );
                  })()}
                  {/* Understanding tier (TVG A/B/C) — the session-level category,
                      now derived purely from topic understanding; drives the
                      understanding-tier block. Engagement is a separate axis. */}
                  {(() => {
                    const cat = (trace?.progress_review?.student_category
                      || (trace?.advisor?.raw_notes as any)?.student_category || "").toLowerCase();
                    const ev = trace?.progress_review?.category_evidence
                      || (trace?.advisor?.raw_notes as any)?.category_evidence || "";
                    const chip: Record<string, { label: string; cls: string }> = {
                      bright:      { label: "LANDS FIRST-GO (A) · raise the ceiling",     cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
                      hardworking: { label: "LANDS WITH HELP (B) · scaffold each step",  cls: "bg-sky-100 text-sky-800 border-sky-300" },
                      disengaged:  { label: "NOT LANDING YET (C) · rebuild foundations", cls: "bg-amber-100 text-amber-800 border-amber-300" },
                    };
                    const c = chip[cat];
                    return (
                      <div className="flex items-center gap-2 mb-1">
                        <div className="text-[10px] text-slate-500">understanding tier</div>
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${c ? c.cls : "bg-slate-100 text-slate-500 border-slate-300"}`}>
                          {c ? c.label : "unknown · not committed yet"}
                        </span>
                        {c && ev ? <span className="text-[10px] text-slate-400 font-mono">· {String(ev)}</span> : null}
                      </div>
                    );
                  })()}
                  {(trace?.progress_review?.bridge_target || trace?.progress_review?.bridge_reason) && (
                    <div className="mt-1 text-[11px] text-slate-700">
                      <div className="grid grid-cols-[140px_1fr] gap-2">
                        <div className="text-slate-500">bridge_target</div>
                        <div className="font-mono break-words">{trace.progress_review.bridge_target || "—"}</div>
                      </div>
                      <div className="grid grid-cols-[140px_1fr] gap-2">
                        <div className="text-slate-500">bridge_reason</div>
                        <div className="font-mono break-words">{trace.progress_review.bridge_reason || "—"}</div>
                      </div>
                    </div>
                  )}
                  {trace?.progress_review?.quiz_focus && (
                    <div className="mt-1 text-[11px] text-slate-700 grid grid-cols-[140px_1fr] gap-2">
                      <div className="text-slate-500">quiz_focus</div>
                      <div className="font-mono break-words">{trace.progress_review.quiz_focus}</div>
                    </div>
                  )}
                  {(trace?.progress_review?.re_anchor_target || trace?.progress_review?.tangent_acknowledged) && (
                    <div className="mt-1 text-[11px] text-slate-700">
                      <div className="grid grid-cols-[140px_1fr] gap-2">
                        <div className="text-slate-500">re_anchor_target</div>
                        <div className="font-mono break-words">{trace.progress_review.re_anchor_target || "—"}</div>
                      </div>
                      <div className="grid grid-cols-[140px_1fr] gap-2">
                        <div className="text-slate-500">tangent_acknowledged</div>
                        <div className="font-mono break-words">{trace.progress_review.tangent_acknowledged || "—"}</div>
                      </div>
                    </div>
                  )}
                  {(trace?.progress_review?.rescue_problem || trace?.progress_review?.struggle_signal) && (
                    <div className="mt-1 text-[11px] text-slate-700">
                      <div className="grid grid-cols-[140px_1fr] gap-2">
                        <div className="text-slate-500">rescue_problem</div>
                        <div className="font-mono break-words">{trace.progress_review.rescue_problem || "—"}</div>
                      </div>
                      <div className="grid grid-cols-[140px_1fr] gap-2">
                        <div className="text-slate-500">struggle_signal</div>
                        <div className="font-mono break-words">{trace.progress_review.struggle_signal || "—"}</div>
                      </div>
                    </div>
                  )}
                  {trace?.progress_review?.directive_text && (
                    <details className="mt-1">
                      <summary className="cursor-pointer text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                        Directive text injected this turn
                      </summary>
                      <div className="mt-1">
                        <Section title="NEXT_ACTION_DIRECTIVE">{trace.progress_review.directive_text.trim()}</Section>
                      </div>
                    </details>
                  )}
                </div>
              )}

              {hasAdvisor && (() => {
                // Advisor notes re-grouped into TVG's own framing (18/07): what
                // the student knows, what's shaky, what to do next, and the read
                // on the student. Empty / null / bookkeeping fields are hidden;
                // the full raw JSON stays available in a collapsed details.
                const n: Record<string, any> = trace?.advisor?.raw_notes || {};
                const knows = fmtNote(n.element_coverage);
                const knowsEvidence = fmtNote(n.knowledge_evidence);
                const covered = n.max_covered_so_far ?? n.covered_element_count;
                const coveredStr = covered != null && covered !== '' && covered !== 0 ? String(covered) : '';
                const gaps = fmtNote(n.prerequisite_gaps);
                const loops = fmtNote(n.open_loops);
                const rem = n.remediation;
                const remLine = rem && typeof rem === 'object'
                  ? `${rem.topic || ''}${rem.gap_grade ? ` — gap grade ${rem.gap_grade} → ${rem.target_grade || '?'}, next: ${rem.next_concept || '—'}` : ''}`.trim()
                  : fmtNote(rem);
                const strategy = fmtNote(n.strategy);
                const nextMove = fmtNote(n.next_move);
                const curiosityScore = n.true_curiosity_score;
                const trajectory = fmtNote(n.curiosity_trajectory);
                const curiosityLine = [
                  curiosityScore != null && curiosityScore !== '' ? `${curiosityScore}/100` : '',
                  trajectory,
                ].filter(Boolean).join(' · ');
                const interest = fmtNote(n.interest_level);
                const onTheme = fmtNote(n.on_theme);
                // How fast new ideas are landing (first_go / after_retries /
                // not_landing) — the understanding axis behind the category.
                const speedRaw = String((n as any).understanding_speed || '').toLowerCase();
                const understandingSpeed = speedRaw && speedRaw !== 'unknown'
                  ? speedRaw.replace(/_/g, ' ')
                  : '';

                const knowsShow = !!(knows || coveredStr || knowsEvidence);
                const shakyShow = !!(gaps || loops || remLine);
                const nextShow = !!(strategy || nextMove);
                const readShow = !!(curiosityLine || interest || onTheme || understandingSpeed);
                const anyGroup = knowsShow || shakyShow || nextShow || readShow;

                return (
                  <div className="mt-3 pt-2 border-t border-violet-200">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 mb-1">
                      Advisor (fed this turn — written by the previous turn's async run)
                    </div>

                    {!anyGroup && (
                      <div className="text-[11px] text-slate-400 mb-1">
                        Nothing to report yet — the advisor fills this in after the first real exchange.
                      </div>
                    )}

                    <Group title="What the student knows" show={knowsShow}>
                      <Row k="covered" v={knows} />
                      <Row k="parts done" v={coveredStr} />
                      <Row k="evidence" v={knowsEvidence} />
                    </Group>

                    <Group title="What's shaky / gaps" show={shakyShow}>
                      <Row k="gaps" v={gaps} />
                      <Row k="open loops" v={loops} />
                      <Row k="catch-up" v={remLine} />
                    </Group>

                    <Group title="What to do next" show={nextShow}>
                      <Row k="strategy" v={strategy} />
                      <Row k="next move" v={nextMove} />
                    </Group>

                    <Group title="Read on the student" show={readShow}>
                      <Row k="curiosity" v={curiosityLine} />
                      <Row k="engagement" v={interest} />
                      <Row k="understanding" v={understandingSpeed} />
                      <Row k="on theme" v={onTheme} />
                    </Group>

                    {Object.keys(n).length > 0 && (
                      <details className="mt-1">
                        <summary className="cursor-pointer text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                          all raw fields ({Object.keys(n).length})
                        </summary>
                        <div className="mt-1">
                          {Object.entries(n).map(([k, v]) => (
                            <div key={k} className="grid grid-cols-[160px_1fr] gap-2 py-0.5">
                              <div className="text-[11px] text-slate-400">{k}</div>
                              <div className="text-[11px] text-slate-600 break-words whitespace-pre-wrap font-mono">
                                {typeof v === 'string' ? v : JSON.stringify(v)}
                              </div>
                            </div>
                          ))}
                        </div>
                      </details>
                    )}

                    {trace?.advisor?.rendered_block && (
                      <details className="mt-2">
                        <summary className="cursor-pointer text-[10px] font-semibold uppercase tracking-wide text-amber-700">
                          Rendered block (what the coach actually saw in the system prompt)
                        </summary>
                        <div className="mt-1">
                          <Section title="ADVISOR_NOTES">{trace.advisor.rendered_block.trim()}</Section>
                        </div>
                      </details>
                    )}
                  </div>
                );
              })()}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default IntentRouterPanel;
